import { type ReactNode, isValidElement, Children, useState } from "react"
import Markdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { Link as RouterLink } from "react-router"
import { CopyIcon, CheckIcon, LinkIcon } from "lucide-react"
import { cn } from "~/lib/utils"

export interface Heading {
  depth: number
  text: string
  slug: string
}

/**
 * Strip the most common markdown decoration so the text fits in a tight UI
 * surface (card preview, mega-menu meta line, etc). Best-effort: enough for
 * editorial prose, not a full AST walk.
 */
export function markdownToPlainText(source: string): string {
  return source
    .replace(/```[\s\S]*?```/g, "") // fenced code blocks
    .replace(/`([^`]+)`/g, "$1") // inline code
    .replace(/^#+\s+/gm, "") // headings
    .replace(/^[-*]\s+/gm, "") // list markers
    .replace(/^>\s?/gm, "") // blockquotes
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // links
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1") // images
    .replace(/[*_~]/g, "") // bold/italic/strike
    .replace(/\n{2,}/g, " ") // collapse paragraphs
    .trim()
}

export function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[`*_~]/g, "")
    .replace(/&amp;/g, "and")
    .replace(/&/g, "and")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
}

/**
 * Parse h2/h3 headings from a markdown string. Skips fenced code blocks so
 * "## Example" inside a code sample doesn't show up in the TOC.
 */
export function parseHeadings(markdown: string): Heading[] {
  const lines = markdown.split("\n")
  const out: Heading[] = []
  let inFence = false
  for (const raw of lines) {
    if (raw.startsWith("```")) {
      inFence = !inFence
      continue
    }
    if (inFence) continue
    const m = /^(#{2,3})\s+(.+?)\s*#*\s*$/.exec(raw)
    if (!m) continue
    const depth = m[1].length
    // Strip basic inline markdown for the display label.
    const text = m[2]
      .replace(/\[(.+?)\]\([^)]+\)/g, "$1")
      .replace(/[`*_]/g, "")
      .trim()
    out.push({ depth, text, slug: slugifyHeading(text) })
  }
  return out
}

function flattenChildren(children: ReactNode): string {
  if (children == null || children === false) return ""
  if (typeof children === "string" || typeof children === "number") {
    return String(children)
  }
  if (Array.isArray(children)) {
    return children.map(flattenChildren).join("")
  }
  if (isValidElement(children)) {
    return flattenChildren((children.props as { children?: ReactNode }).children)
  }
  return ""
}

function HeadingWithId({
  level,
  children,
  linkable = true,
  idPrefix,
}: {
  level: 2 | 3 | 4
  children: ReactNode
  linkable?: boolean
  idPrefix?: string
}) {
  const text = flattenChildren(children)
  const id = idPrefix ? `${idPrefix}-${slugifyHeading(text)}` : slugifyHeading(text)
  // Match the Typography H2 style (`text-xl font-normal text-primary mb-4`)
  // so markdown `##` headings look the same as page-level `<H2>` headers
  // (e.g. "Datasets using this metric"). h3/h4 step down the size but keep
  // the same weight + color.
  const className =
    level === 2
      ? "text-xl font-normal text-primary mt-10 mb-4 scroll-mt-20"
      : level === 3
        ? "text-lg font-normal text-primary mt-6 mb-3 scroll-mt-20"
        : "text-base font-normal text-primary mt-4 mb-2 scroll-mt-20"
  const Tag = `h${level}` as "h2" | "h3" | "h4"
  // Clickable anchor that jumps to (and shares) this heading. Hidden until the
  // heading is hovered/focused. A plain `<a href="#slug">` so the browser does
  // the native in-page scroll to `id` — works inside the docs' own scroll
  // container, and updates the URL hash for copy-paste deep links.
  return (
    <Tag id={id} className={cn("group/heading relative", className)}>
      {linkable && (
        <a
          href={`#${id}`}
          aria-label={`Link to "${text}"`}
          className="absolute -left-5 top-1/2 -translate-y-1/2 inline-flex items-center text-muted-foreground no-underline opacity-0 transition-opacity group-hover/heading:opacity-100 focus:opacity-100"
        >
          <LinkIcon className="h-4 w-4" />
        </a>
      )}
      {children}
    </Tag>
  )
}

function CodeBlock({ children }: { children: ReactNode }) {
  const [copied, setCopied] = useState(false)
  const text = flattenChildren(children).replace(/\n$/, "")

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard access denied or unsupported — silent no-op.
    }
  }

  return (
    <div className="group relative my-4">
      <pre className="rounded-md border bg-muted/60 text-sm leading-relaxed p-4 pr-12 overflow-x-auto text-foreground">
        {Children.map(children, (child) =>
          isValidElement(child) ? child : <span>{child}</span>,
        )}
      </pre>
      <button
        type="button"
        onClick={copy}
        aria-label={copied ? "Copied" : "Copy code"}
        className={cn(
          "absolute top-2 right-2 inline-flex items-center justify-center h-7 w-7 rounded-md border bg-background/80 backdrop-blur",
          "text-muted-foreground hover:text-foreground hover:bg-accent",
          "opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity",
        )}
      >
        {copied ? (
          <CheckIcon className="h-3.5 w-3.5 text-primary" />
        ) : (
          <CopyIcon className="h-3.5 w-3.5" />
        )}
      </button>
    </div>
  )
}

/**
 * Renders an authored markdown body in the docs prose style. Headings get
 * stable IDs so the right-rail TOC can scroll-spy them.
 */
export function MarkdownBody({
  source,
  headingLinks = true,
  headingOffset = 0,
  idPrefix,
}: {
  source: string
  // The hover link icon beside each heading. The Release Log turns it off:
  // its sections are short and not worth linking to one by one.
  headingLinks?: boolean
  // Steps every heading down this many levels (capped at h4), for bodies
  // shown under a page heading of their own, like Release Log notes. Done
  // on the parsed tree, so `##` inside code blocks is left alone.
  headingOffset?: 0 | 1
  // Prefixed to heading ids, so several bodies on one page don't repeat ids
  // or take over an anchor the page uses itself.
  idPrefix?: string
}) {
  const level = (n: 2 | 3 | 4) => Math.min(n + headingOffset, 4) as 2 | 3 | 4
  return (
    <div className="prose dark:prose-invert max-w-none prose-headings:scroll-mt-20 prose-code:before:content-none prose-code:after:content-none">
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <HeadingWithId level={level(2)} linkable={headingLinks} idPrefix={idPrefix}>
              {children}
            </HeadingWithId>
          ),
          h2: ({ children }) => (
            <HeadingWithId level={level(2)} linkable={headingLinks} idPrefix={idPrefix}>
              {children}
            </HeadingWithId>
          ),
          h3: ({ children }) => (
            <HeadingWithId level={level(3)} linkable={headingLinks} idPrefix={idPrefix}>
              {children}
            </HeadingWithId>
          ),
          h4: ({ children }) => (
            <HeadingWithId level={level(4)} linkable={headingLinks} idPrefix={idPrefix}>
              {children}
            </HeadingWithId>
          ),
          p: ({ children }) => <p className="leading-relaxed my-3">{children}</p>,
          ul: ({ children }) => <ul className="list-disc ml-6 space-y-1 my-3">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal ml-6 space-y-1 my-3">{children}</ol>,
          li: ({ children }) => <li className="leading-relaxed">{children}</li>,
          hr: () => <hr className="my-8 border-border" />,
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-primary/40 bg-muted/30 px-4 py-2 my-4 text-muted-foreground rounded-r">
              {children}
            </blockquote>
          ),
          a: ({ href, children }) => {
            if (!href) return <span>{children}</span>
            const isExternal = /^https?:\/\//.test(href) || href.startsWith("mailto:")
            if (isExternal) {
              return (
                <a
                  href={href}
                  target={href.startsWith("mailto:") ? undefined : "_blank"}
                  rel="noreferrer"
                  className="text-primary font-normal no-underline hover:underline"
                >
                  {children}
                </a>
              )
            }
            // Strip a trailing `.md` so links authored as `(/docs/api.md)` still
            // resolve to the live route.
            const to = href.replace(/\.md(#|$)/, "$1")
            return (
              <RouterLink to={to} className="text-primary font-normal no-underline hover:underline">
                {children}
              </RouterLink>
            )
          },
          code: ({ className, children, ...props }) => {
            const isBlock = /language-/.test(className ?? "") || flattenChildren(children).includes("\n")
            if (isBlock) {
              return (
                <code className={cn(className, "block font-mono text-foreground")} {...props}>
                  {children}
                </code>
              )
            }
            return (
              <code
                className="rounded border bg-muted px-1.5 py-0.5 text-[0.85em] font-mono text-foreground"
                {...props}
              >
                {children}
              </code>
            )
          },
          pre: ({ children }) => <CodeBlock>{children}</CodeBlock>,
        }}
      >
        {source}
      </Markdown>
    </div>
  )
}
